'use client';
import {createUseStyles} from 'react-jss';
import {FC, MouseEvent, ReactNode, useRef, useState} from 'react';
import {Accordion, AccordionDetails} from '@mui/material';
import {colorPalette} from 'styles/colors';
import {useTranslation} from 'react-i18next';
import {DescribedText} from 'features/terms/components/described-text';
import {Button} from 'common/components/button';
import {StringUtils} from 'common/utils/string-utils';
import {CssBreakpoints} from 'styles/responsive';
import {ChevronDownIcon, ChevronUpIcon} from '@navikt/aksel-icons';
import {getEnv} from 'environment/public.environment';

const useExpansionPanelStyles = createUseStyles(theme => ({
  container: {
    margin: '2rem 0',
    backgroundColor: theme.colors.accent,

    '@media print': {
      background: 'transparent',

      '& .MuiCollapse-wrapper': {
        display: 'contents',
      },
    },
  },
  accordion: {
    '&.MuiAccordion-root': {
      backgroundColor: 'transparent',
      boxShadow: 'unset',
    },
    '@media print': {
      '& div.MuiCollapse-root': {
        visibility: 'visible',
        overflow: 'visible',
        height: 'auto !important',
      },
    },
  },
  header: {
    backgroundColor: colorPalette.mainBlue80,
    color: colorPalette.grey05,
    flexDirection: 'row-reverse',
    lineHeight: '1.5',
    padding: '10px 20px',

    '& .MuiAccordionSummary-content': {
      margin: 0,
      userSelect: 'text',
      display: 'inline',
    },

    '& .MuiAccordionSummary-expandIconWrapper': {
      transition: 'none',
    },

    '@media print': {
      color: 'black',
      background: 'none',
      borderBottom: '1pt solid black',
      padding: '0',
    },
    [`@media (max-width: ${CssBreakpoints.XS}px)`]: {
      paddingLeft: '1rem',
    },
    '&:hover:not(.Mui-disabled)': {
      cursor: 'unset',
    },
  },
  headerTitle: {
    fontSize: '1.25rem',
    lineHeight: '1.2em',
    margin: '0',
    fontWeight: '600',
    display: 'inline',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: '400',
    display: 'inline-block',
  },
  content: {
    padding: '0 2rem',
    '@media print': {
      padding: '0',
    },
    [`@media (max-width: ${CssBreakpoints.XS}px)`]: {
      paddingLeft: '0.5rem',
    },
  },
  alwaysVisible: {
    '@media print': {
      breakInside: 'avoid',
      pageBreakInside: 'avoid',
    },
  },
  detailBtnWrapper: {
    marginLeft: '1em',
  },
  hrExpansionPanel: {
    border: 0,
    borderTop: `1px solid${theme.colors.separator}`,
    marginBottom: 0,

    '@media print': {
      display: 'none',
    },
  },
  [`@media (max-width: ${CssBreakpoints.MD}px)`]: {
    hrExpansionPanel: {
      marginTop: '1.5rem',
    },
  },
}));

interface ExpansionPanelProps {
  children: ReactNode;
  id: string;
  title: string;
  subtitle: string;
  titleDescription?: ReactNode;
  defaultContent: ReactNode;
  index: number;
}

export const ExpansionPanel: FC<ExpansionPanelProps> = ({
  id,
  title,
  subtitle,
  defaultContent,
  titleDescription,
  children,
  index,
}) => {
  const styles = useExpansionPanelStyles();
  const [expanded, setExpanded] = useState(false);
  const {t} = useTranslation(['common']);

  const panelRef = useRef<HTMLDivElement>(null);

  const toggleExpansionPanel = (e: MouseEvent<HTMLElement>) => {
    const expandBtn = document.getElementById(`toggle_${id}`);
    if (
      window?.getSelection()?.toString().length === 0 ||
      e?.target === expandBtn ||
      expandBtn?.contains(e?.target as HTMLElement)
    ) {
      setExpanded(!expanded);
      panelRef?.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }
  };

  return (
    <article className={styles.container}>
      <Accordion disableGutters className={styles.accordion} expanded={expanded} ref={panelRef} id={`accordion_${id}`}>
        <div className={styles.alwaysVisible}>
          <summary
            className={styles.header}
            id={`header_${id}`}
            // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
            role='none'
            data-testid='expanded-title'
          >
            <span className='print-only'>{index + 1}) </span>
            {getEnv().features.fellesDatakatalog && titleDescription ? (
              <DescribedText description={titleDescription} overrideNormalTextColor={false}>
                <h2 className={styles.headerTitle}>{StringUtils.capitalize(title)}</h2>
              </DescribedText>
            ) : (
              <h2 className={styles.headerTitle}>{StringUtils.capitalize(title)}</h2>
            )}
            , <div className={styles.subtitle}>{subtitle}</div>
          </summary>
          <AccordionDetails className={styles.content}>{defaultContent}</AccordionDetails>
          <hr className={styles.hrExpansionPanel} aria-hidden />
        </div>
        <AccordionDetails className={styles.content} id={`${id}-expanded-content`}>
          {children}
        </AccordionDetails>
      </Accordion>
      <div className={styles.detailBtnWrapper}>
        <Button
          variant='simple'
          name={`toggle-expansion-panel-${expanded ? 'close' : 'open'}`}
          id={`toggle_${id}`}
          onClick={toggleExpansionPanel}
          icon={expanded ? ChevronUpIcon : ChevronDownIcon}
          aria-expanded={expanded}
          aria-controls={`${id}-expanded-content`}
        >
          {expanded ? t('common:hide-details') : t('common:show-details')}
        </Button>
      </div>
    </article>
  );
};
